import { aggregate } from '@/providers/backendClient';

export const createTestTherapistIdsMatch = async (
  isHiddenTestAccount: string,
) => {
  if (isHiddenTestAccount !== 'hidden') {
    return {};
  }

  const pipeline = [
    {
      $match: {
        deletedAt: null,
        isHiddenTestAccount: true,
      },
    },
    {
      $project: {
        _id: 1,
      },
    },
  ];

  const { therapists } = await aggregate<{
    therapists: Array<{ _id: string }>;
  }>('therapists', { therapists: pipeline });

  const testTherapistIds = therapists.map(
    (therapist: any) => therapist._id,
  );

  return { therapistId: { $nin: testTherapistIds } };
};

export const createTherapistSearchMatch = async (
  therapistEmail: string,
  therapistFirstName: string,
  therapistLastName: string,
  isHiddenTestAccount: string,
) => {
  const hasTherapistSearch =
    !!therapistEmail || !!therapistFirstName || !!therapistLastName;

  if (isHiddenTestAccount === 'hidden' && !hasTherapistSearch) {
    return {};
  }

  const emailMatch = therapistEmail ? { email: therapistEmail } : {};

  const firstNameMatch = therapistFirstName
    ? {
        $expr: {
          $eq: [
            { $trim: { input: '$firstName' } },
            therapistFirstName?.trim() || '',
          ],
        },
      }
    : {};

  const lastNameMatch = therapistLastName
    ? {
        $expr: {
          $eq: [
            { $trim: { input: '$lastName' } },
            therapistLastName?.trim() || '',
          ],
        },
      }
    : {};

  const testAccountMatch = (() => {
    if (isHiddenTestAccount === 'only') {
      return { isHiddenTestAccount: true };
    }

    return {};
  })();

  const hasNameMatch = !!therapistFirstName || !!therapistLastName;

  const filter = {
    deletedAt: null,
    ...emailMatch,
    ...testAccountMatch,
  };

  if (Object.keys(filter).length === 1 && !hasNameMatch) {
    return {};
  }

  const nameMatchingPipeline = hasNameMatch
    ? [
        {
          $match: {
            ...firstNameMatch,
            ...lastNameMatch,
          },
        },
        {
          $project: {
            deletedAt: 1,
            isHiddenTestAccount: 1,
            firstName: 1,
            lastName: 1,
          },
        },
      ]
    : [];

  const pipeline = [
    {
      $match: filter,
    },
    ...nameMatchingPipeline,
    {
      $project: {
        _id: 1,
      },
    },
  ];

  const { therapists } = await aggregate('therapists', {
    therapists: pipeline,
  });

  const therapistIds = therapists.map(
    (therapist: any) => therapist._id,
  );

  return { therapistId: { $in: therapistIds } };
};

export const createDeletedAtMatch = (deletedAt = 'hidden') => {
  if (deletedAt === 'hidden') {
    return { deletedAt: null };
  }

  if (deletedAt === 'only') {
    return { deletedAt: { $ne: null } };
  }

  return {};
};

// Creates a MongoDB match object for filtering draft text entries
// Returns different match conditions based on the draftText parameter:
// - "only": Show only draft text entries
// - "hidden": Hide all draft text entries
// - "visible" or undefined: Show all entries (no filtering)
export const createDraftTextMatch = (draftText = 'hidden') => {
  if (draftText === 'only') {
    return {
      mediaType: 'text',
      status: 'draft',
    };
  }

  if (draftText === 'hidden') {
    return {
      $or: [
        { mediaType: { $ne: 'text' } },
        { status: { $ne: 'draft' } },
      ],
    };
  }

  return {};
};

export const createDefaultTherapistsFilter = async (
  filterValues: any,
) => {
  const {
    therapistEmail,
    isHiddenTestAccount = 'hidden',
    therapistFirstName,
    therapistLastName,
    deletedAt = 'hidden',
    draftText = 'hidden',
    ...filter
  } = filterValues;

  const deletedAtMatch = createDeletedAtMatch(deletedAt);
  const draftTextMatch = createDraftTextMatch(draftText);

  console.log('deletedAtMatch', deletedAtMatch);
  console.log('draftTextMatch', draftTextMatch);

  const testTherapistIdsMatch = await createTestTherapistIdsMatch(
    isHiddenTestAccount,
  );

  console.log('testTherapistIdsMatch', testTherapistIdsMatch);

  const therapistSearchMatch = await createTherapistSearchMatch(
    therapistEmail,
    therapistFirstName,
    therapistLastName,
    isHiddenTestAccount,
  );

  console.log('therapistSearchMatch', therapistSearchMatch);

  const $and = [therapistSearchMatch, testTherapistIdsMatch].filter(
    obj => Object.keys(obj).length > 0,
  );

  const combinedFilter = {
    ...filter,
    ...deletedAtMatch,
    ...draftTextMatch,
    ...($and.length > 0 ? { $and } : {}),
  };

  console.log('combinedFilter', combinedFilter);

  return combinedFilter;
};
